import { useEffect, useState } from "react"
import { Link, useOutletContext } from "react-router-dom"
import { User, Shield } from "lucide-react"
import { UserDetails } from "../../../../types"
import MyAccountInfo from "../sections/MyAccountInfo"
import OtherInfo from "../sections/OtherInfo"
import CancelSubscription from "../sections/CancelSubscription"
import { useAppDispatch, useAppSelector, useToast } from "../../../hooks"
import {
  selectProfileUpdated,
  toggleProfileUpdated
} from "../../../../redux/slices/auth"
import {
  useFetchLoanOfficerQuery,
  useUpdateLoanOfficerMutation
} from "../../../../redux/queries"
import ImageUploader from "../../image-uploader"
import { checkIsBankAdmin } from "../../../utils"

interface OutletContext {
  user: UserDetails
}

const MyAccountSettings = () => {
  const { user } = useOutletContext<OutletContext>()
  const isBankAdmin = checkIsBankAdmin(user?.roles || [])

  const dispatch = useAppDispatch()
  const { pushErrorToast, pushSuccessToast } = useToast()

  const profileUpdated = useAppSelector(selectProfileUpdated)
  const [editingSection, setEditingSection] = useState<"info">()

  const [updateLoanOfficer, { isSuccess, isError }] =
    useUpdateLoanOfficerMutation()

  const { data: loDetails } = useFetchLoanOfficerQuery(user.id, {
    skip: !user.id
  })

  useEffect(() => {
    if (isSuccess) {
      pushSuccessToast("Your account has been updated.")
      setEditingSection(undefined)
      dispatch(toggleProfileUpdated(true))
    }
    if (isError) {
      pushErrorToast("Failed to update account.")
    }
  }, [isSuccess, isError, pushErrorToast, pushSuccessToast, dispatch])

  const handleClickEditInfo = () =>
    setEditingSection(editingSection === "info" ? undefined : "info")

  useEffect(() => {
    if (!editingSection && !profileUpdated) return
    dispatch(toggleProfileUpdated(false))
  }, [dispatch, editingSection, profileUpdated])

  const handleUpdateEmail = (newEmail: string) => {
    if (!loDetails) return
    updateLoanOfficer({
      ...loDetails,
      email: newEmail
    })
  }

  const handleUpdateAvatar = (pictureURL: string) => {
    if (!loDetails) return
    updateLoanOfficer({
      ...loDetails,
      picture: pictureURL
    })
  }

  const handleUpdateNotification = (enable: boolean) => {
    if (!loDetails) return
    updateLoanOfficer({
      ...loDetails,
      notifications_enabled: enable
    })
  }

  return (
    <div className="px-6">
      <h2 className="font-bold text-xl py-6">My account settings</h2>

      <div className="px-4">
        <ImageUploader
          buttonLabel="Change"
          imageUrl={
            loDetails?.picture
              ? `${loDetails?.picture}?v=${loDetails?.updated}`
              : ""
          }
          onChangeImageUrl={handleUpdateAvatar}
          placeholder={<User size={96} color="#e5e5e5" />}
        />

        <MyAccountInfo
          info={user}
          inEditMode={editingSection === "info"}
          toggleEdit={handleClickEditInfo}
          onUpdateEmail={handleUpdateEmail}
        />
        <div className="pt-12">
          <div className="flex items-center justify-between pb-4 border-b border-neutral-200">
            <div className="text-lg font-bold">Password & Security</div>
          </div>
          <div className="pt-4">
            <Link
              to="/account/security"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600"
            >
              <Shield size={16} />
              Manage password, email, and MFA in Account Security
            </Link>
          </div>
        </div>

        <OtherInfo
          info={user}
          isNotificationsEnabled={loDetails?.notifications_enabled}
          onEnableNotification={handleUpdateNotification}
        />

        {isBankAdmin && <CancelSubscription />}
      </div>
    </div>
  )
}

export default MyAccountSettings
